import Tracker from "../../../../prop-trackers/tracker.js";
import ValueTracker from "../../../../prop-trackers/value-tracker.js";
import DictTracker from "../../../../prop-trackers/dict-tracker.js";
import SinglePropTracker from "../../../../prop-trackers/single-prop-tracker.js";
import { ForEach, ValueTracker as ForEachValueTracker } from "../../../../prop-tracker-utils/for-each.js";
import PropChainTracker from "../../../../prop-tracker-utils/prop-chain-tracker.js";
import ValueTrackerObjGen from "../../../../prop-tracker-utils/value-tracker-obj-gen.js";

function isEmpty(obj) {
    for (const key in obj) {
        return false;
    }

    return true;
}

function getFirstKey(obj) {
    for (const key in obj) {
        return key;
    }
}

class LabelDictGenerator extends Tracker {
    constructor(labelTracker, wrapCtx, evtCtx) {
        super();

        this._wrapCtx = wrapCtx;

        this._forEach = new ForEach(labelTracker, (key) => {
            return key === "length" ? new LengthTracker() : new ObjectLabelTracker(wrapCtx, evtCtx)
        });

        this._outputData = {};
        this._outputTracker = new DictTracker();

        const needsUpdate = () => this.protected_needsUpdate();
        this._forEach.listenNeedsUpdate(needsUpdate);
    }

    _setLabelData(labelKey, objectKey, labelIndex, labelData) {
        let labelKeyLabels = this._outputData[labelKey];

        if (!labelKeyLabels) {
            labelKeyLabels = {};
            this._outputData[labelKey] = labelKeyLabels;
        }

        let objKeyLabels = labelKeyLabels[objectKey];

        if (!objKeyLabels) {
            objKeyLabels = {};
            labelKeyLabels[objectKey] = objKeyLabels;
        }

        if (labelIndex in objKeyLabels) {
            objKeyLabels[labelIndex].label = labelData;
        } else {
            objKeyLabels[labelIndex] = this._wrapCtx.getWrapper({
                object_uuid: objectKey,
                label: labelData,
            });
        }
    }

    _deleteLabelData(labelKey, objectKey, labelIndex) {
        const labelKeyLabels = this._outputData[labelKey];
        if (!labelKeyLabels) return;

        const objKeyLabels = labelKeyLabels[objectKey];
        if (!objKeyLabels) return;

        delete objKeyLabels[labelIndex];

        if (isEmpty(objKeyLabels)) {
            delete labelKeyLabels[objectKey];

            if (isEmpty(labelKeyLabels)) {
                delete this._outputData[labelKey];
            }
        }
    }

    _updateLabelData(labelKey) {
        const labelKeyLabels = this._outputData[labelKey];

        if (labelKeyLabels) {
            const objectKey = getFirstKey(labelKeyLabels);
            const objKeyLabels = labelKeyLabels[objectKey];

            const labelIndex = getFirstKey(objKeyLabels);
            const label = objKeyLabels[labelIndex];

            this._outputTracker.setValue(labelKey, label);
        } else {
            this._outputTracker.deleteValue(labelKey);
        }
    }

    protected_startUpdate() {
        this._forEach.update(({ trackers }) => {
            for (const objLabelTracker of trackers) {
                objLabelTracker.update(({ trackers }) => {
                    for (const labelTracker of trackers) {
                        labelTracker.update(({ userdata: labelIndex, prop: { oldExists, oldValue, newExists, newValue } }) => {
                            let oldLabelKey, newLabelKey;

                            if (oldExists) {
                                const { objectKey, labelKey, labelData } = oldValue;
                                this._deleteLabelData(labelKey, objectKey, labelIndex);
                                oldLabelKey = labelKey;
                            }

                            if (newExists) {
                                const { objectKey, labelKey, labelData } = newValue;
                                this._setLabelData(labelKey, objectKey, labelIndex, labelData);
                                newLabelKey = labelKey;
                            }

                            if (oldLabelKey !== undefined) {
                                this._updateLabelData(oldLabelKey);
                            }

                            if (newLabelKey !== undefined && newLabelKey !== oldLabelKey) {
                                this._updateLabelData(newLabelKey);
                            }
                        });
                    }
                });
            }
        });

        return this._outputTracker.startUpdate();
    }

    protected_finishUpdate() {
        return this._outputTracker.finishUpdate();
    }
}

const NoSubTrackers = Object.freeze({ trackers: [] });

class LengthTracker extends ForEachValueTracker {
    constructor() {
        super();
    }

    setValue(objectLabelData) {}
    deleteValue() {}

    protected_startUpdate() {
        return NoSubTrackers;
    }

    protected_finishUpdate() {}
}

class ObjectLabelTracker extends ForEachValueTracker {
    constructor(wrapCtx, evtCtx) {
        super();

        this._evtCtx = evtCtx;

        this._labelsTracker = new PropChainTracker(evtCtx);

        this._keyTracker = new SinglePropTracker();
        this._keyGen = new ValueTrackerObjGen(wrapCtx, evtCtx, this._keyTracker);

        this._forEach = new ForEach(this._labelsTracker, (key) => {
            return key === "length" ? new ObjectLabelLengthTracker() : new LabelTracker(key, evtCtx, this._keyGen);
        });

        const needsUpdate = () => this.protected_needsUpdate();
        this._keyTracker.listenNeedsUpdate(needsUpdate);
        this._forEach.listenNeedsUpdate(needsUpdate);
    }

    setValue(objectLabelData) {
        if (objectLabelData) {
            this._keyTracker.track(objectLabelData, this._evtCtx.getEvents(objectLabelData), "object_uuid");
            this._labelsTracker.track(objectLabelData, ["label"]);
        } else {
            this.deleteValue();
        }
    }

    deleteValue() {
        this._keyTracker.track();
        this._labelsTracker.track();
    }

    protected_startUpdate() {
        this._keyGen.update();

        return this._forEach.startUpdate();
    }

    protected_finishUpdate() {
        return this._forEach.finishUpdate();
    }
}

class LabelTracker extends ForEachValueTracker {
    constructor(key, evtCtx, objKeyGen) {
        super();

        this._evtCtx = evtCtx;
        this._objKeyGen = objKeyGen;

        this._objKeyTracker = new SinglePropTracker();
        this._labelKeyTracker = new SinglePropTracker();
        this._dataTracker = new ValueTracker();
        this._outputTracker = new ValueTracker(key);

        const needsUpdate = () => this.protected_needsUpdate();
        this._objKeyTracker.listenNeedsUpdate(needsUpdate);
        this._labelKeyTracker.listenNeedsUpdate(needsUpdate);
        this._dataTracker.listenNeedsUpdate(needsUpdate);
    }

    setValue(labelData) {
        if (labelData) {
            this._objKeyTracker.track(this._objKeyGen.data, this._objKeyGen.events, "value");
            this._labelKeyTracker.track(labelData, this._evtCtx.getEvents(labelData), "uuid");
            this._dataTracker.setValue(labelData);
        } else {
            this.deleteValue();
        }
    }

    deleteValue() {
        this._objKeyTracker.track();
        this._labelKeyTracker.track();
        this._dataTracker.setValue();
    }

    protected_startUpdate() {
        this._objKeyTracker.update(({ prop: { newExists: objectKeyExists, newValue: objectKey } }) => {
            this._labelKeyTracker.update(({ prop: { newExists: labelKeyExists, newValue: labelKey } }) => {
                this._dataTracker.update(({ prop: { newValue: labelData } }) => {
                    if (objectKeyExists && labelKeyExists && labelData) {
                        this._outputTracker.setValue({ objectKey, labelKey, labelData });
                    } else {
                        this._outputTracker.deleteValue();
                    }
                });
            });
        });

        return this._outputTracker.startUpdate();
    }

    protected_finishUpdate() {
        return this._outputTracker.finishUpdate();
    }
}

const NoValue = Object.freeze({ prop: { newExists: false, newValue: undefined } });

class ObjectLabelLengthTracker extends ForEachValueTracker {
    constructor() {
        super();
    }

    setValue(labelData) {}
    deleteValue() {}

    protected_startUpdate() {
        return NoValue;
    }

    protected_finishUpdate() {}
}

export default LabelDictGenerator;
